import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';

import { BaseIndexPageComponent } from 'src/app/components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from 'src/app/components/pages/base-index-page/base-index-page.config';
import { DataTableColumnText } from 'src/app/components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import { DataTableColumnBadge } from 'src/app/components/widgets/data-table/columns/data-table-column-badge/data-table-column-badge.config';
import { DataTableColumnLink } from 'src/app/components/widgets/data-table/columns/data-table-column-link/data-table-column-link.config';
import { DataTableColumnActions } from 'src/app/components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import ApiOptionsConfig from 'src/app/services/api/api-options.config';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import {
  Regulator,
  RegulatorClient,
  RegulatorClientWebsite,
  Scan,
  regulatorStatusBadgeClass,
  scanComplianceBadgeClass,
  scanComplianceLabel,
  scanStatusWithStageLabel,
  scanTriggerTypeLabel
} from 'src/app/models/entities/regulator';
import { RegulatorContextBoxComponent, RegulatorContextItem } from '../components/regulator-context-box/regulator-context-box.component';
import { apiErrorMessage, toWebsiteHref } from '../regulators-ui.utils';

/** Page size used to resolve the website (there is no single-website GET endpoint). */
const WEBSITES_LOOKUP_PAGE_SIZE = 1000;

@Component({
  selector: 'app-regulator-website-scans-page',
  imports: [BaseIndexPageComponent, RegulatorContextBoxComponent],
  templateUrl: './regulator-website-scans-page.component.html',
  styleUrl: './regulator-website-scans-page.component.scss',
})
export class RegulatorWebsiteScansPageComponent implements OnInit, OnDestroy {

  @ViewChild(BaseIndexPageComponent) private indexPage?: BaseIndexPageComponent;

  baseIndexPageConfig: BaseIndexPageConfig;
  contextItems: RegulatorContextItem[] = [];
  regulator: Regulator | null = null;
  isStartingScan = false;

  readonly regulatorId: number;
  readonly clientId: number;
  readonly websiteId: number;

  private subscriptions = new Subscription();

  constructor(
    protected regulatorsService: RegulatorsService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.regulatorId = Number(this.route.snapshot.paramMap.get('regulatorId'));
    this.clientId = Number(this.route.snapshot.paramMap.get('clientId'));
    this.websiteId = Number(this.route.snapshot.paramMap.get('websiteId'));

    this.baseIndexPageConfig = {
      pageTitle: 'Regulators',
      cardTitle: 'Scan History',
      dataTable: {
        data: [],
        pagination: null,
        searchModel: {
          status: null,
          isCompliant: null,
        },
        columns: [
          new DataTableColumnText({
            attribute: 'createTime',
            header: 'Created',
            width: '170px',
            value: (item: Scan) => this.commonService.formatDateValue(item?.externalCreatedAt ?? item?.createTime, true)
          }),
          new DataTableColumnBadge({
            attribute: 'status',
            header: 'Status',
            filter: 'select',
            filterOptionsSource: 'scanStatuses',
            value: (item: Scan) => item?.status ? scanStatusWithStageLabel(item) : null,
            badgeClass: (item: Scan) => regulatorStatusBadgeClass(item?.status)
          }),
          new DataTableColumnBadge({
            attribute: 'isCompliant',
            header: 'Compliance',
            filter: 'select',
            filterOptionsSource: 'scanComplianceOptions',
            value: (item: Scan) => scanComplianceLabel(item?.isCompliant),
            badgeClass: (item: Scan) => scanComplianceBadgeClass(item?.isCompliant)
          }),
          new DataTableColumnLink({
            attribute: 'finalUrl',
            header: 'Final URL',
            sortable: false,
            value: (item: Scan) => item?.finalUrl ?? '',
            href: (item: Scan) => toWebsiteHref(item?.finalUrl)
          }),
          new DataTableColumnText({
            attribute: 'triggerType',
            header: 'Trigger',
            width: '120px',
            value: (item: Scan) => scanTriggerTypeLabel(item?.triggerType)
          }),
          new DataTableColumnText({
            attribute: 'completedAt',
            header: 'Completed',
            width: '170px',
            value: (item: Scan) => this.commonService.formatDateValue(item?.completedAt, true)
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            sortable: false,
            cellClass: 'text-center',
            style: { width: '100px' },
            buttons: [
              {
                icon: 'fa-eye',
                tooltip: 'View scan detail',
                aclAction: 'view',
                onClick: (item: Scan) => this.routeService.regulatorWebsiteScanDetail(
                  this.regulatorId, this.clientId, this.websiteId, item.scanId
                )
              },
            ]
          }),
        ]
      },
      apiListService: (options: ApiOptionsConfig) =>
        this.regulatorsService.listScans(this.regulatorId, this.clientId, this.websiteId, options),
      responseListModelName: 'scans',
    };
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        const canModify = this.accessControlService.hasFeatureActionAccess(profileCode, 'REGULATORS_LIST', 'modify');
        this.baseIndexPageConfig.addNewItemButton = canModify
          ? { type: 'primary', title: 'Scan now', onClick: () => this.scanNow() }
          : undefined;
      })
    );

    const websitesParams = new HttpParams().set('page', 0).set('size', WEBSITES_LOOKUP_PAGE_SIZE);
    forkJoin({
      regulator: this.regulatorsService.detail(this.regulatorId),
      client: this.regulatorsService.detailClient(this.regulatorId, this.clientId),
      websites: this.regulatorsService.listClientWebsites(this.regulatorId, this.clientId, { params: websitesParams }),
    }).subscribe({
      next: ({ regulator, client, websites }: any) => {
        const website = (websites?.data?.websites ?? [])
          .find((w: RegulatorClientWebsite) => w.websiteId === this.websiteId) ?? null;
        this.applyContext(regulator?.data?.regulator, client?.data?.client, website);
      },
      error: () => this.router.navigate(['/error/404'])
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.mainLayoutService.setBreadcrumbs(null);
  }

  goBack(): void {
    this.routeService.regulatorClientWebsites(this.regulatorId, this.clientId);
  }

  scanNow(): void {
    if (this.regulator && !this.regulator.externalCode) {
      this.alertNotificationService.warning('Scans cannot be started: the regulator has no external code configured.');
      return;
    }
    if (this.isStartingScan) {
      return;
    }

    this.isStartingScan = true;
    this.regulatorsService.startScan(this.regulatorId, this.clientId, this.websiteId).subscribe({
      next: () => {
        this.isStartingScan = false;
        this.alertNotificationService.success('Scan queued');
        this.indexPage?.reload();
      },
      error: (error: any) => {
        this.isStartingScan = false;
        this.alertNotificationService.error(apiErrorMessage(error, 'Error starting the scan'));
      }
    });
  }

  private applyContext(
    regulator: Regulator | null | undefined,
    client: RegulatorClient | null | undefined,
    website: RegulatorClientWebsite | null
  ): void {
    if (!regulator || !client) {
      return;
    }

    this.regulator = regulator;
    const websiteLabel = website?.url ?? `#${this.websiteId}`;
    this.baseIndexPageConfig.cardTitle = `Scan History – ${websiteLabel}`;
    this.contextItems = [
      { label: 'Regulator', code: regulator.code, value: regulator.name },
      { label: 'Client', code: client.code, value: client.name },
      { label: 'Website', value: websiteLabel },
    ];
    this.mainLayoutService.setBreadcrumbs([
      { label: 'Regulators', routerLink: '/regulators' },
      { label: regulator.code },
      { label: 'Clients', routerLink: `/regulators/${regulator.regulatorId}/clients` },
      { label: client.name, routerLink: `/regulators/${regulator.regulatorId}/clients/${client.clientId}` },
      { label: websiteLabel },
      { label: 'Scans' },
    ]);
  }
}
